/**
 * FILE-OVERVIEW:
 * - 役割: 共通ヘッダー。画面タイトルとスケジュール/メモ切替を提供。
 * - 主な連携: react-router, CustomSwitchまたはButtonUI
 * - 本番運用上の注意: 切替パス変更時は既存リンクとの後方互換を保持する。
 */

import React from 'react';
import { Box, Button, ButtonGroup, Typography, IconButton, useMediaQuery } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useLocation } from 'react-router-dom';

const AppHeader = ({ onMenuToggle, isSidebarOpen = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width:1023px)');
  const isMemo = location.pathname === '/memo';
  const showSidebarMenuButton = isMobile && typeof onMenuToggle === 'function';

  const handleViewChange = (event) => {
    const newPath = event.target.checked ? '/memo' : '/';
    navigate(newPath);
  };

  const title = isMemo
    ? '取材メモ管理'
    : '取材スケジュール管理';

  const accentColor = isMemo ? '#188038' : '#1a73e8';

  const navButtonSx = (active, activeColor) => ({
    minWidth: isMobile ? 76 : 104,
    px: isMobile ? 1 : 1.75,
    py: isMobile ? 0.6 : 0.75,
    borderRadius: 999,
    textTransform: 'none',
    fontWeight: 700,
    fontSize: isMobile ? '0.82rem' : '0.9rem',
    letterSpacing: '0.02em',
    color: active ? '#ffffff' : '#3c4043',
    bgcolor: active ? activeColor : '#ffffff',
    borderColor: active ? activeColor : '#d6dce5',
    boxShadow: active
      ? `0 2px 6px ${activeColor}33`
      : '0 1px 2px rgba(60,64,67,0.18)',
    '&:hover': {
      bgcolor: active ? activeColor : '#f1f3f4',
      borderColor: active ? activeColor : '#c7d0db',
      boxShadow: '0 2px 8px rgba(60,64,67,0.22)',
    },
  });

  const handleMemoClick = () => {
    navigate('/memo');
  };

  const handleScheduleClick = () => {
    navigate('/');
  };

  return (
    <Box
      className="app-header"
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'center',
        maxWidth: 1920,
        width: '100%',
        mx: 'auto',
        px: isMobile ? 1.5 : 2.5,
        py: isMobile ? 1.25 : 1.5,
        bgcolor: '#ffffff',
        color: '#1f1f1f',
        position: 'sticky',
        top: 0,
        zIndex: 1200,
        borderBottom: '1px solid #e5e9f0',
        boxShadow: '0 1px 2px rgba(60,64,67,0.16), 0 1px 3px 1px rgba(60,64,67,0.08)',
        gap: isMobile ? 1 : 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isMobile ? 'center' : 'flex-start',
          gap: 1.25,
          minWidth: 0,
          width: isMobile ? '100%' : 'auto',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            bgcolor: accentColor,
            boxShadow: `0 0 0 4px ${isMemo ? 'rgba(24,128,56,0.14)' : 'rgba(26,115,232,0.14)'}`,
            flexShrink: 0,
          }}
        />
        <Typography
          variant="h6"
          sx={{
            fontSize: isMobile ? '0.95rem' : '1.2rem',
            fontWeight: 600,
            letterSpacing: '0.01em',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textAlign: isMobile ? 'center' : 'left',
          }}
        >
          {title}
        </Typography>

        {showSidebarMenuButton && (
          <IconButton
            aria-label={isSidebarOpen ? 'close sidebar' : 'open sidebar'}
            onClick={() => onMenuToggle(!isSidebarOpen)}
            sx={{
              position: isMobile ? 'absolute' : 'static',
              right: isMobile ? 0 : 'auto',
              ml: isMobile ? 0 : 1,
              color: '#3c4043',
            }}
          >
            {isSidebarOpen ? <CloseIcon /> : <MenuIcon />}
          </IconButton>
        )}
      </Box>

      <Box
        sx={{
          mr: 0,
          ml: isMobile ? 0 : 1,
          mt: isMobile ? 0.5 : 0,
          px: 0.5,
          py: 0.4,
          borderRadius: 999,
          bgcolor: '#f8fafd',
          border: '1px solid #e3e6ea',
          boxShadow: 'inset 0 1px 2px rgba(60,64,67,0.06)',
          alignSelf: isMobile ? 'center' : 'auto',
        }}
      >
        <ButtonGroup
          variant="outlined"
          disableElevation
          sx={{
            '& .MuiButtonGroup-grouped': {
              borderColor: 'transparent',
              '&:not(:last-of-type)': {
                borderRightColor: 'transparent',
              },
            },
          }}
        >
          <Button
            onClick={handleScheduleClick}
            sx={navButtonSx(!isMemo, '#1a73e8')}
          >
            スケジュール
          </Button>
          <Button
            onClick={handleMemoClick}
            sx={navButtonSx(isMemo, '#188038')}
          >
            メモ
          </Button>
        </ButtonGroup>
      </Box>
    </Box>
  );
};

export default AppHeader;